const LOGIC_MAP_SCHEMA_VERSION = 'invention-logic-map/v1'

const NODE_STYLE_MAP = {
  overview: { fill: '#2c2d4d', stroke: '#2c2d4d', labelFill: '#ffffff', fontSize: 13, fontWeight: 700, radius: 8 },
  primaryProblem: { fill: '#f3f4f6', stroke: '#f3f4f6', labelFill: '#111827', fontSize: 12, fontWeight: 600, radius: 7 },
  secondaryProblem: { fill: '#f3f4f6', stroke: '#f3f4f6', labelFill: '#111827', fontSize: 11, fontWeight: 600, radius: 7 },
  coreMeasure: { fill: '#ffffff', stroke: '#ffffff', labelFill: '#111827', fontSize: 11, fontWeight: 600, radius: 4 },
  additionalMeasure: { fill: '#ffffff', stroke: '#ffffff', labelFill: '#111827', fontSize: 11, fontWeight: 600, radius: 4 },
  primaryEffect: { fill: '#ffffff', stroke: '#ffffff', labelFill: '#111827', fontSize: 11, fontWeight: 600, radius: 4 },
  additionalEffect: { fill: '#ffffff', stroke: '#ffffff', labelFill: '#111827', fontSize: 11, fontWeight: 600, radius: 4 },
  detail: { fill: '#ffffff', stroke: '#ffffff', labelFill: '#374151', fontSize: 11, fontWeight: 500, radius: 4 },
}

const TYPE_ORDER = {
  overview: 0,
  primaryProblem: 1,
  secondaryProblem: 2,
  coreMeasure: 3,
  additionalMeasure: 4,
  primaryEffect: 5,
  additionalEffect: 6,
  detail: 7,
}

const parseJson = (value, fallback) => {
  if (value && typeof value === 'object') {
    return value
  }
  if (typeof value !== 'string' || !value.trim()) {
    return fallback
  }
  try {
    return JSON.parse(value)
  } catch (error) {
    return fallback
  }
}

const stripLegacyQuestionPrefix = (value = '') => {
  return String(value || '')
    .replace(/^\s*\d+(?:\.\d+)?[.:：、\s]+/, '')
    .trim()
}

const normalizeNodeType = (value, fallback = 'detail') => {
  const raw = String(value || fallback || 'detail').trim()
  if (Object.prototype.hasOwnProperty.call(NODE_STYLE_MAP, raw)) {
    return raw
  }
  if (/主|primary/i.test(raw)) return 'primaryProblem'
  if (/次|secondary/i.test(raw)) return 'secondaryProblem'
  if (/核心|core/i.test(raw)) return 'coreMeasure'
  if (/手段|measure|solution/i.test(raw)) return 'additionalMeasure'
  if (/效果|effect/i.test(raw)) return 'additionalEffect'
  return fallback || 'detail'
}

const normalizeParagraphIndices = (value) => {
  if (!Array.isArray(value)) {
    return []
  }
  return Array.from(new Set(
    value
      .map(item => Number(item?.paragraphIndex ?? item))
      .filter(item => Number.isInteger(item))
  ))
}

const normalizeNode = (node, fallbackId, fallbackType = 'detail') => {
  if (!node || typeof node !== 'object') {
    return null
  }

  const label = String(node.label || node.name || node.title || node.problem || node.text || '').trim()
  if (!label) {
    return null
  }

  const nodeType = normalizeNodeType(node.nodeType || node.type, fallbackType)
  const children = Array.isArray(node.children)
    ? node.children
      .map((child, index) => normalizeNode(child, `${fallbackId}-${index + 1}`))
      .filter(Boolean)
      .sort((a, b) => (TYPE_ORDER[a.nodeType] ?? 99) - (TYPE_ORDER[b.nodeType] ?? 99))
    : []

  return {
    id: String(node.id || fallbackId),
    label,
    nodeType,
    description: String(node.description || ''),
    paragraphIndices: normalizeParagraphIndices(node.paragraphIndices || node.sourceParagraphIndices || node.sources),
    children,
  }
}

const createEmptyMap = () => ({
  schemaVersion: LOGIC_MAP_SCHEMA_VERSION,
  title: '发明构思总览',
  summary: '',
  nodeCount: 0,
  legacyItems: [],
  root: {
    id: 'root',
    label: '发明构思总览',
    nodeType: 'overview',
    children: [],
  },
})

const countNodes = (node) => {
  if (!node) return 0
  return 1 + (node.children || []).reduce((sum, child) => sum + countNodes(child), 0)
}

const legacyListToMap = (items) => {
  const map = createEmptyMap()
  const legacyItems = Array.isArray(items) ? items : []
  map.legacyItems = legacyItems
  map.root.children = legacyItems
    .map((item, index) => {
      const primary = item?.primary === true || item?.nodeType === 'primaryProblem' || index === 0
      const problem = item?.problem || item?.question || item?.primaryProblem || item?.secondaryProblem || `发明构思 ${index + 1}`
      const measure = item?.measure || item?.solution || item?.scheme || item?.technicalFeature || item?.answer || ''
      const effect = item?.effect || item?.technicalEffect || item?.result || ''
      const children = []
      if (measure) {
        children.push({
          id: `legacy-${index + 1}-measure`,
          label: String(measure).trim(),
          nodeType: primary ? 'coreMeasure' : 'additionalMeasure',
        })
      }
      if (effect) {
        children.push({
          id: `legacy-${index + 1}-effect`,
          label: String(effect).trim(),
          nodeType: primary ? 'primaryEffect' : 'additionalEffect',
        })
      }
      return normalizeNode({
        id: `legacy-${index + 1}`,
        label: stripLegacyQuestionPrefix(problem),
        nodeType: primary ? 'primaryProblem' : 'secondaryProblem',
        children,
      }, `legacy-${index + 1}`, primary ? 'primaryProblem' : 'secondaryProblem')
    })
    .filter(Boolean)
  map.nodeCount = countNodes(map.root)
  return map
}

export const normalizeInventIdeaLogicMap = (value) => {
  const parsed = parseJson(value, null)
  if (!parsed) {
    return createEmptyMap()
  }
  if (Array.isArray(parsed)) {
    return legacyListToMap(parsed)
  }

  const root = normalizeNode(parsed.root, 'root', 'overview')
  if (!root) {
    return createEmptyMap()
  }

  const map = {
    schemaVersion: parsed.schemaVersion || LOGIC_MAP_SCHEMA_VERSION,
    title: parsed.title || root.label || '发明构思总览',
    summary: parsed.summary || '',
    sourceVersion: parsed.sourceVersion || '',
    updatedAt: parsed.updatedAt || '',
    legacyItems: [],
    root: {
      ...root,
      id: root.id || 'root',
      nodeType: 'overview',
    },
  }
  map.nodeCount = countNodes(map.root)
  return map
}

const stripDisplayPrefix = (value = '') => {
  return String(value || '')
    .replace(/^(主技术问题|主要技术问题|主要问题|次要技术问题\d*|次要技术问题|次要问题|核心解决手段|主要方案|附加解决手段|附加方案|主要技术效果|主要效果|附加技术效果|附加效果)[：:]\s*/, '')
    .trim()
}

const firstChildTextByTypes = (children = [], nodeTypes = []) => {
  const matched = children.find(child => nodeTypes.includes(child.nodeType))
  return stripDisplayPrefix(matched?.label || matched?.description || '')
}

export const buildInventIdeaTextBlocks = (logicMap) => {
  const map = logicMap || createEmptyMap()
  const problemNodes = map.root?.children || []

  return problemNodes
    .map((node, index) => {
      const primary = node.nodeType === 'primaryProblem' || index === 0
      const children = node.children || []
      return {
        id: node.id || `invent-idea-${index + 1}`,
        variant: primary ? 'primary' : 'secondary',
        problemLabel: primary ? '主要问题' : '次要问题',
        measureLabel: primary ? '主要方案' : '附加方案',
        effectLabel: primary ? '主要效果' : '附加效果',
        problem: stripDisplayPrefix(node.label || node.description),
        measure: firstChildTextByTypes(children, primary ? ['coreMeasure', 'additionalMeasure', 'detail'] : ['additionalMeasure', 'coreMeasure', 'detail']),
        effect: firstChildTextByTypes(children, primary ? ['primaryEffect', 'additionalEffect'] : ['additionalEffect', 'primaryEffect']),
        paragraphIndices: node.paragraphIndices || [],
      }
    })
    .filter(block => block.problem || block.measure || block.effect)
}

export const hasInventIdeaLogicMap = (logicMap) => {
  return Boolean(buildInventIdeaTextBlocks(logicMap).length)
}
